import React, { useState, useMemo } from "react";
import moment from "moment";
import { Tooltip } from "antd";
import { formatUSD } from "../../utils/helpers/formatUSD";
import "./Calendar.scss";

const Calendar = ({ data = {} }) => {
    const [currentMonth, setCurrentMonth] = useState(moment());
    const [selectedDate, setSelectedDate] = useState(moment());
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Handle window resize
    React.useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Process data into daily stats
    const dailyStats = useMemo(() => {
        const stats = {};
        Object.keys(data).forEach((dateKey) => {
            const dayData = data[dateKey];
            stats[dateKey] = {
                count: dayData.no_of_trades || 0,
                totalProfit: dayData.net_pnl || 0,
                trades: [],
            };
        });
        return stats;
    }, [data]);

    const renderHeader = () => {
        return (
            <div className="calendar-header">
                <button
                    className="nav-button"
                    onClick={() => setCurrentMonth(moment(currentMonth).subtract(1, "month"))}
                >
                    &#8249;
                </button>
                <div className="current-month">
                    {currentMonth.format("MMMM YYYY")}
                </div>
                <button
                    className="nav-button"
                    onClick={() => setCurrentMonth(moment(currentMonth).add(1, "month"))}
                >
                    &#8250;
                </button>
            </div>
        );
    };

    const renderDays = () => {
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        return (
            <div className="days-row">
                {days.map((day, index) => (
                    <div key={index} className="day-name">
                        {day}
                    </div>
                ))}
            </div>
        );
    };

    const renderCells = () => {
        const monthStart = moment(currentMonth).startOf("month");
        const monthEnd = moment(currentMonth).endOf("month");
        const startDate = moment(monthStart).startOf("week");
        const endDate = moment(monthEnd).endOf("week");

        const rows = [];
        let days = [];
        let day = moment(startDate);

        while (day.isSameOrBefore(endDate)) {
            for (let i = 0; i < 7; i++) {
                const formattedDate = day.format("YYYY-MM-DD");
                const cloneDay = moment(day);
                const dayStats = dailyStats[formattedDate];

                const isProfit = dayStats && dayStats.totalProfit > 0;
                const isLoss = dayStats && dayStats.totalProfit < 0;
                const hasData = dayStats && dayStats.count > 0;

                const cellContent = (
                    <div
                        className={`calendar-cell ${!day.isSame(currentMonth, "month") ? "disabled" : ""
                            } ${day.isSame(selectedDate, "day") ? "selected" : ""} ${hasData ? (isProfit ? "profit" : isLoss ? "loss" : "") : ""
                            }`}
                        key={day.format("YYYY-MM-DD")}
                        onClick={() => setSelectedDate(moment(cloneDay))}
                    >
                        <span className="date-number">{day.format("D")}</span>
                        {hasData && !isMobile && (
                            <div className="day-stats">
                                <div className="trades-count">
                                    <span className="label">Trades:</span>
                                    <span className="value">{dayStats.count}</span>
                                </div>
                                <div
                                    className={`pnl ${isProfit ? "profit-text" : "loss-text"}`}
                                >
                                    <span className="label">P&L:</span>
                                    <span className="value">{formatUSD(dayStats.totalProfit)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                );

                if (hasData && isMobile) {
                    days.push(
                        <Tooltip
                            key={day.format("YYYY-MM-DD")}
                            title={
                                <div>
                                    <div><strong>Trades:</strong> {dayStats.count}</div>
                                    <div><strong>P&L:</strong> {formatUSD(dayStats.totalProfit)}</div>
                                </div>
                            }
                        >
                            {cellContent}
                        </Tooltip>
                    );
                } else {
                    days.push(cellContent);
                }

                day = moment(day).add(1, "day");
            }
            rows.push(
                <div className="calendar-row" key={day.format("YYYY-MM-DD")}>
                    {days}
                </div>
            );
            days = [];
        }

        return <div className="calendar-body">{rows}</div>;
    };

    return (
        <div className="eventCalendar">
            {renderHeader()}
            {renderDays()}
            {renderCells()}
        </div>
    );
};

export default Calendar;
